import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Collapse,
} from '@mui/material';
import {
  UserCircleGear,
  Heart,
  CheckCircle,
  WarningCircle,
  Question,
  TrendUp,
} from '@phosphor-icons/react';
import { FC, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../utils/redux/redux';
import { createSelector } from '@reduxjs/toolkit';

interface GeneralSupportSidebarProps {
  selectedSection: string;
  selectedSubSection: string;
  onSectionChange: (section: string, subSection?: string) => void;
  templateName: string;
}

const selectStepStatus = createSelector(
  (state: RootState) => state.generalSupport.faqSetup.status,
  faqSetup => ({
    'faq-setup': faqSetup,
  })
);

const selectStepStatuses = createSelector(
  (state: RootState) => state.userJourney.assistantDefinition.status,
  (state: RootState) => state.userJourney.personalization.status,
  (state: RootState) => state.userJourney.callStages.status,
  (assistantDefinition, personalization, callStages) => ({
    'assistant-definition': assistantDefinition,
    personalization: personalization,
    analytics: callStages,
  })
);

const GeneralSupportSidebar: FC<GeneralSupportSidebarProps> = ({
  selectedSection,
  selectedSubSection,
  onSectionChange,
  templateName,
}) => {
  const stepStatuses = useSelector(selectStepStatuses);
  const faqSetupStatus = useSelector(selectStepStatus);
  const [expandedItem, setExpandedItem] = useState<string>(selectedSection);

  const sidebarItems = [
    {
      id: 'faq-setup',
      label: 'FAQ Setup',
      icon: <Question size={16} />,
      subItems: [{ id: 'select-preference', label: 'Select Preference' }],
    },
    {
      id: 'assistant-definition',
      label: 'Agent Definition',
      icon: <UserCircleGear size={16} />,
      subItems: [
        { id: 'assistant-name', label: 'Agent Name' },
        { id: 'behavior', label: 'Behavior' },
        { id: 'timezone', label: 'Timezone' },
      ],
    },
    {
      id: 'personalization',
      label: 'Personalization',
      icon: <Heart size={16} />,
      subItems: [
        { id: 'greeting', label: 'Greeting' },
        { id: 'identity', label: 'Identity' },
        { id: 'job', label: 'Job' },
        { id: 'call-action', label: 'Call Action' },
      ],
    },
    {
      id: 'analytics',
      label: (
        <>
          Analytics{' '}
          <i style={{ fontSize: '12px', fontWeight: 300 }}>(optional)</i>
        </>
      ),
      icon: <TrendUp size={16} />,
      subItems: [{ id: 'call-stage', label: 'Call Stages' }],
    },
  ];

  useEffect(() => {
    setExpandedItem(selectedSection);
  }, [selectedSection]);

  const getStatusIcon = (itemId: string) => {
    const status = stepStatuses[itemId as keyof typeof stepStatuses];

    if (!status) return null;

    if (status.isComplete) {
      return (
        <CheckCircle
          size={16}
          weight="fill"
          color="#4CAF50"
          style={{ marginLeft: 'auto' }}
        />
      );
    }
    if (status.hasError) {
      return (
        <WarningCircle
          size={16}
          weight="fill"
          color="#f44336"
          style={{ marginLeft: 'auto' }}
        />
      );
    }
    return null;
  };

  const getFaqSetupStatusIcon = (itemId: string) => {
    const status = faqSetupStatus[itemId as keyof typeof faqSetupStatus];
    if (!status) return null;
    if (status.isComplete) {
      return <CheckCircle size={16} weight="fill" color="#4CAF50" />;
    }
    if (status.hasError) {
      return <WarningCircle size={16} weight="fill" color="#f44336" />;
    }
    return null;
  };

  const handleItemClick = (itemId: string) => {
    if (expandedItem === itemId) {
      setExpandedItem('');
    } else {
      setExpandedItem(itemId);
    }
    onSectionChange(itemId);
  };

  return (
    <Box
      sx={{
        width: '232px',
        height: '100%',
        borderRight: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography
          variant="h6"
          color="custom.textBlue"
          sx={{ fontSize: '16px', fontWeight: 500 }}
        >
          {templateName}
        </Typography>
      </Box>
      <List sx={{ flex: 1, width: '100%' }}>
        {sidebarItems.map(item => (
          <Box key={item.id}>
            <ListItem
              disablePadding
              sx={{
                borderBottom: expandedItem !== item.id ? '1px solid' : 'none',
                borderColor: 'divider',
                p: 0,
              }}
            >
              <ListItemButton
                selected={selectedSection === item.id}
                onClick={() => handleItemClick(item.id)}
                sx={{
                  color: 'custom.textBlue',
                  '&.Mui-selected': {
                    backgroundColor: 'transparent',
                  },
                  '&:hover': { backgroundColor: 'transparent' },
                }}
              >
                <ListItemIcon
                  sx={{
                    color:
                      selectedSection === item.id
                        ? 'custom.textBlue'
                        : 'lightGray.text',
                    minWidth: '24px',
                    fontWeight: selectedSection === item.id ? 600 : 400,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  sx={{
                    '& .MuiTypography-root': {
                      color:
                        selectedSection === item.id
                          ? 'custom.textBlue'
                          : 'lightGray.text',
                      fontWeight: selectedSection === item.id ? 600 : 400,
                      fontSize: '14px',
                      transition: 'all 0.2s ease',
                    },
                  }}
                />
                {getStatusIcon(item.id)}
                {item.id === 'faq-setup' && getFaqSetupStatusIcon(item.id)}
              </ListItemButton>
            </ListItem>
            {item.subItems.length > 0 && (
              <Collapse
                in={expandedItem === item.id}
                timeout="auto"
                unmountOnExit
              >
                <List
                  disablePadding
                  sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
                >
                  {item.subItems.map(subItem => (
                    <ListItem key={subItem.id} disablePadding>
                      <ListItemButton
                        selected={selectedSubSection === subItem.id}
                        onClick={() => onSectionChange(item.id, subItem.id)}
                        sx={{
                          pl: 6,
                          '&.Mui-selected': { backgroundColor: 'transparent' },
                          '&:hover': { backgroundColor: 'transparent' },
                        }}
                      >
                        <ListItemText
                          primary={subItem.label}
                          sx={{
                            '& .MuiTypography-root': {
                              fontSize: '14px',
                              color: 'lightGray.text',
                            },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </Box>
        ))}
      </List>
    </Box>
  );
};

export default GeneralSupportSidebar;
